import { useChat } from "@/context/ChatContext";
import { useAuth } from "@/context/AuthContext";
import { canSendMessage } from "@/services/subscriptionService";
import { DocumentAnalysis } from "@/context/chat/documentAnalysisService";
import { useCreditsUsage } from "@/hooks/useCreditsUsage";
import { useStorageUsage } from "@/hooks/useStorageUsage";
import { toast } from "@/hooks/use-toast";
import { TokenLimitDialog } from "@/components/TokenLimitDialog";
import { LimitReachedDialog } from "@/components/LimitReachedDialog";
import { useVoiceRecording } from "@/hooks/useVoiceRecording";
import { PromptInputBox } from "@/components/ui/ai-prompt-box";
import ChatEnterInput from "./ChatEnterInput";
import ImageThumbnail from "./ImageThumbnail";
import FileThumbnail from "./FileThumbnail";
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

interface ChatInputProps {
  selectedFile: File | null;
  setSelectedFile: React.Dispatch<React.SetStateAction<File | null>>;
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  isUserApproved: boolean;
  onDocumentAnalysis?: (analysis: DocumentAnalysis) => void;
  triggerNativeFileSelector?: () => void;
  onNonImageFileSelected?: (file: File) => void;
}

const ChatInput: React.FC<ChatInputProps> = ({
  selectedFile,
  setSelectedFile,
  inputValue,
  setInputValue,
  textareaRef,
  isUserApproved,
  onDocumentAnalysis,
  triggerNativeFileSelector,
  onNonImageFileSelected,
}) => {
  const { sendMessage, isProcessing, currentConversation, messages, fetchMessages, createNewConversation } = useChat();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isLimitReached, refetch: refetchCredits } = useCreditsUsage();
  const { isStorageLimitReached, refetch: refetchStorage } = useStorageUsage();
  const [canSend, setCanSend] = useState<boolean>(true);
  const [nextMessageTime, setNextMessageTime] = useState<Date | null>(null);
  const [timeLeft, setTimeLeft] = useState<string>("");
  
  const [isFileProcessing, setIsFileProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState<'thinking' | 'extracting' | 'analyzing' | 'completed' | null>(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [showTokenLimitDialog, setShowTokenLimitDialog] = useState(false);
  const [showStorageLimitDialog, setShowStorageLimitDialog] = useState(false);
  const [limitDialogType, setLimitDialogType] = useState<'credits' | 'storage'>('credits');
  const previousProcessingState = useRef(isProcessing);
  const shouldFocusAfterSend = useRef(false);

  // Estado para preview de múltiplas imagens
  const [imagePreviews, setImagePreviews] = useState<{url: string, file: File}[]>([]);
  
  // Estado para múltiplos arquivos pendentes
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  // Effect to handle selectedFile changes from ChatInterface
  useEffect(() => {
    // Se um arquivo não-imagem foi definido via selectedFile (upload via botão), mover para pendingFiles
    if (selectedFile && !selectedFile.type.startsWith('image/')) {
      console.log('📎 Arquivo não-imagem detectado em selectedFile, movendo para pendingFiles:', selectedFile.name);
      setPendingFiles(prev => [...prev, selectedFile]);
      setSelectedFile(null); // Limpar selectedFile para evitar conflito
      
      // Notificar callback se existir
      if (onNonImageFileSelected) {
        onNonImageFileSelected(selectedFile);
      }
    }
  }, [selectedFile, onNonImageFileSelected, setSelectedFile]);

  // Função para gerar preview de imagem
  const generateImagePreview = (file: File) => {
    if (file && file.type.startsWith('image/')) {
      const previewUrl = URL.createObjectURL(file);
      setImagePreviews(prev => [...prev, {url: previewUrl, file}]);
    }
  };

  // Função para remover imagem específica
  const handleRemoveSpecificImage = (index: number) => {
    setImagePreviews(prev => {
      const imageToRemove = prev[index];
      if (imageToRemove) {
        URL.revokeObjectURL(imageToRemove.url);
      }
      const newPreviews = prev.filter((_, i) => i !== index);
      
      // Se não há mais previews e o selectedFile é uma imagem, limpar selectedFile
      if (newPreviews.length === 0 && selectedFile?.type.startsWith('image/')) {
        setSelectedFile(null);
      }
      
      return newPreviews;
    });
  };

  // Limpar previews quando não há arquivo selecionado
  useEffect(() => {
    if (!selectedFile) {
      // Limpar todos os previews
      imagePreviews.forEach(preview => {
        URL.revokeObjectURL(preview.url);
      });
      setImagePreviews([]);
    } else if (selectedFile.type.startsWith('image/')) {
      // Se é uma nova imagem e não está nos previews, adicionar
      const alreadyExists = imagePreviews.some(preview => preview.file.name === selectedFile.name);
      if (!alreadyExists) {
        generateImagePreview(selectedFile);
      }
    }
  }, [selectedFile]);

  // Cleanup dos previews ao desmontar componente
  useEffect(() => {
    return () => {
      imagePreviews.forEach(preview => {
        URL.revokeObjectURL(preview.url);
      });
    };
  }, []);

  useEffect(() => {
    // Verifica se o usuário pode enviar mensagens
    const checkMessageLimit = async () => {
      if (!user) return;
      
      try {
        const result = await canSendMessage(user.id);
        setCanSend(result.canSend);
        setNextMessageTime(result.nextAvailableTime || null);
      } catch (error) {
        console.error("Erro ao verificar limite de mensagens:", error);
      }
    };
    
    checkMessageLimit();
    
    // Configura um intervalo para verificar periodicamente
    const interval = setInterval(checkMessageLimit, 60000); // Verifica a cada minuto
    
    return () => clearInterval(interval);
  }, [user]);

  // Listener para múltiplos documentos selecionados via botão
  useEffect(() => {
    const handleMultipleDocuments = (event: CustomEvent) => {
      const { files } = event.detail;
      if (files && files.length > 0) {
        setPendingFiles(prev => [...prev, ...files]);
      }
    };
    
    window.addEventListener('multipleDocumentsSelected', handleMultipleDocuments as EventListener);
    
    return () => {
      window.removeEventListener('multipleDocumentsSelected', handleMultipleDocuments as EventListener);
    };
  }, []);

  // Listener para múltiplas imagens selecionadas via botão
  useEffect(() => {
    const handleMultipleImages = (event: CustomEvent) => {
      const { files } = event.detail;
      if (files && files.length > 0) {
        // Usar a mesma lógica do handlePasteFile - processar todas as imagens
        setSelectedFile(files[0]); // Primeira imagem
        files.slice(1).forEach(file => generateImagePreview(file)); // Demais imagens
      }
    };
    
    window.addEventListener('multipleImagesSelected', handleMultipleImages as EventListener);
    
    return () => {
      window.removeEventListener('multipleImagesSelected', handleMultipleImages as EventListener);
    };
  }, []);

  useEffect(() => {
    // Atualiza a contagem regressiva
    if (!nextMessageTime) return;
    
    const updateCountdown = () => {
      const now = new Date();
      if (now >= nextMessageTime) {
        setCanSend(true);
        setNextMessageTime(null);
        setTimeLeft("");
        return;
      }

      const diff = nextMessageTime.getTime() - now.getTime();
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTimeLeft(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };
    
    updateCountdown();
    const countdownInterval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(countdownInterval);
  }, [nextMessageTime]);

  // Focus management after message sending
  useEffect(() => {
    // Se estava processando e agora não está mais, restaurar foco
    if (previousProcessingState.current && !isProcessing && shouldFocusAfterSend.current) {
      console.log('Processing completed - restoring focus to input');
      textareaRef.current?.focus();
      shouldFocusAfterSend.current = false;
    }
    previousProcessingState.current = isProcessing;
  }, [isProcessing, canSend, isUserApproved]);

  // Handle file paste from clipboard
  const handlePasteFile = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!isUserApproved || !canSend || isFileProcessing) return;
    
    const clipboardData = e.clipboardData;
    if (!clipboardData || !clipboardData.files) return;
    
    const files = Array.from(clipboardData.files);
    
    // Tipos de arquivo aceitos
    const validTypes = [
      'application/pdf',
      'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    
    // **CORREÇÃO**: Processar TODOS os arquivos válidos colados
    const validFiles = files.filter(file => validTypes.includes(file.type));
    
    if (validFiles.length > 0) {
      console.log(`📋 ${validFiles.length} arquivo(s) colado(s) via CTRL+V:`, validFiles.map(f => f.name));
      
      // Separar imagens de documentos
      const imageFiles = validFiles.filter(file => file.type.startsWith('image/'));
      const documentFiles = validFiles.filter(file => !file.type.startsWith('image/'));
      
      // Para imagens: usar selectedFile para a primeira e adicionar o resto via generateImagePreview
      if (imageFiles.length > 0) {
        setSelectedFile(imageFiles[0]); // Primeira imagem para compatibilidade
        
        // Adicionar imagens extras diretamente aos previews
        imageFiles.slice(1).forEach(file => {
          generateImagePreview(file);
        });
      }
      
      // Para documentos: adicionar todos aos pendingFiles
      if (documentFiles.length > 0) {
        setPendingFiles(prev => [...prev, ...documentFiles]);
      }
      
      // Prevent default paste behavior
      e.preventDefault();
      
      toast({
        title: validFiles.length > 1 ? "Arquivos Colados" : "Arquivo Colado",
        description: validFiles.length > 1 
          ? `${validFiles.length} arquivos prontos para análise.`
          : `${validFiles[0].name} pronto para análise.`,
        variant: "default",
      });
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) return;

    // Check storage limit before accepting files
    if (isStorageLimitReached()) {
      toast({
        title: "Armazenamento Cheio",
        description: "Você atingiu o limite de armazenamento. Exclua arquivos antigos ou faça upgrade do plano.",
        variant: "destructive",
      });
      return;
    }

    const validFiles = droppedFiles.filter(file => {
      const validTypes = [
        'application/pdf',
        'image/png',
        'image/jpeg',
        'image/jpg',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ];
      
      const maxSize = 50 * 1024 * 1024; // 50MB
      
      if (!validTypes.includes(file.type)) {
        toast({
          title: "Tipo de arquivo não suportado",
          description: `${file.name} não é um tipo de arquivo válido.`,
          variant: "destructive",
        });
        return false;
      }
      
      if (file.size > maxSize) {
        toast({
          title: "Arquivo muito grande",
          description: `${file.name} excede o limite de 50MB.`,
          variant: "destructive",
        });
        return false;
      }
      
      return true;
    });

    if (validFiles.length > 0) {
      setPendingFiles(prev => [...prev, ...validFiles]);
      toast({
        title: "Arquivos Adicionados",
        description: `${validFiles.length} arquivo(s) prontos para envio.`,
        variant: "default",
      });
    }
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check storage limit before accepting files
    if (isStorageLimitReached()) {
      toast({
        title: "Armazenamento Cheio",
        description: "Você atingiu o limite de armazenamento. Exclua arquivos antigos ou faça upgrade do plano.",
        variant: "destructive",
      });
      return;
    }

    const validFiles = files.filter(file => {
      const validTypes = [
        'application/pdf',
        'image/png',
        'image/jpeg',
        'image/jpg',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ];
      
      const maxSize = 50 * 1024 * 1024; // 50MB
      
      if (!validTypes.includes(file.type)) {
        toast({
          title: "Tipo de arquivo não suportado",
          description: `${file.name} não é um tipo de arquivo válido.`,
          variant: "destructive",
        });
        return false;
      }
      
      if (file.size > maxSize) {
        toast({
          title: "Arquivo muito grande",
          description: `${file.name} excede o limite de 50MB.`,
          variant: "destructive",
        });
        return false;
      }
      
      return true;
    });

    if (validFiles.length > 0) {
      setPendingFiles(prev => [...prev, ...validFiles]);
      toast({
        title: "Arquivos Adicionados",
        description: `${validFiles.length} arquivo(s) prontos para envio.`,
        variant: "default",
      });
    }
    
    // Reset input value
    e.target.value = '';
  }, []);

  // 🔒 GUARD: Prevenir double-submit
  const inFlightRef = useRef(false);

  // 🔒 Sistema de idempotência para criação de conversa
  const ensureConversationPromiseRef = useRef<Promise<string | null> | null>(null);
  const pendingConversationIdRef = useRef<string | null>(null);

  const ensureConversation = useCallback(async (): Promise<string | null> => {
    // GUARD: Validar autenticação primeiro
    if (!user?.id) {
      console.error('❌ User not authenticated');
      return null;
    }

    // 1) Já existe conversa? Retorna o ID
    if (currentConversation?.id) {
      return currentConversation.id;
    }

    // 2) Já está criando? Reutiliza a mesma promise
    if (ensureConversationPromiseRef.current) {
      console.log('⏳ Conversa já está sendo criada, aguardando...');
      return ensureConversationPromiseRef.current;
    }

    // 4) Criar nova conversa (uma única vez)
    console.log('🆕 Iniciando criação de conversa...');
    
    const promise = (async () => {
      const id = await createNewConversation();
      
      if (!id) {
        console.error('❌ Falha ao criar conversa');
        return null;
      }

      pendingConversationIdRef.current = id;
      
      // Aguardar estado estabilizar
      await new Promise(resolve => setTimeout(resolve, 200));
      
      return id;
    })().finally(() => {
      ensureConversationPromiseRef.current = null;
    });

    ensureConversationPromiseRef.current = promise;
    return promise;
  }, [currentConversation?.id, createNewConversation, user?.id, navigate, location.pathname]);

  const handleSendMessage = async (textOverride?: string, fileOverride?: File) => {
    const messageText = (textOverride ?? inputValue)?.trim();
    const messageFile = fileOverride || selectedFile;
    
    if (!isUserApproved || (!messageText && !messageFile && pendingFiles.length === 0) || isProcessing || !canSend || isFileProcessing) return;
    
    // 🔒 IDEMPOTÊNCIA: Bloquear envios duplicados
    if (inFlightRef.current) {
      console.log('⚠️ [DEDUPE] Envio já em andamento, bloqueando duplicata');
      return;
    }
    
    inFlightRef.current = true;
    
    // Check credit limit before sending
    if (isLimitReached()) {
      setLimitDialogType('credits');
      setShowTokenLimitDialog(true);
      return;
    }

    // Check storage limit if sending files
    if ((messageFile || pendingFiles.length > 0) && isStorageLimitReached()) {
      setLimitDialogType('storage');
      setShowStorageLimitDialog(true);
      return;
    }
    
    console.log("[ENTER-FIX] submit", { length: messageText?.length ?? 0 });
    
    // ✅ Garantir conversa existente (idempotente)
    const conversationId = await ensureConversation();
    
    if (!conversationId) {
      console.error('❌ Failed to ensure conversation');
      toast({
        title: "Erro",
        description: "Não foi possível criar uma nova conversa.",
        variant: "destructive",
      });
      inFlightRef.current = false;
      return;
    }
    
    // Marcar que devemos focar após o envio
    shouldFocusAfterSend.current = true;
    
    // Se há arquivos pendentes, processar múltiplos arquivos
    if (pendingFiles.length > 0) {
      await handlePendingFilesProcessing();
    } else {
      // Envio normal para arquivo único
      await sendMessage(messageText, messageFile);
    }
    
    setInputValue("");
    setSelectedFile(null);
    setPendingFiles([]);
    
    // Após enviar uma mensagem, verifica novamente o limite e atualiza tokens
    if (user) {
      const result = await canSendMessage(user.id);
      setCanSend(result.canSend);
      setNextMessageTime(result.nextAvailableTime || null);
      
      // Atualiza o uso de créditos e armazenamento
      refetchCredits();
      refetchStorage();
    }
    
    // 🔒 Liberar lock após envio completo
    inFlightRef.current = false;
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter") return;
    // @ts-ignore - isComposing exists in modern browsers
    if (e.isComposing) return; // Respeitar IME (input methods asiáticos)
    if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return; // Shift+Enter = quebra linha
    e.preventDefault();
    handleSendMessage();
  };

  const handleAttachmentClick = () => {
    if (!isUserApproved || !canSend || isFileProcessing) return;

    // Check storage limit before opening file selector
    if (isStorageLimitReached()) {
      setLimitDialogType('storage');
      setShowStorageLimitDialog(true);
      return;
    }
    
    if (triggerNativeFileSelector) {
      triggerNativeFileSelector();
    } else {
      // Fallback para comportamento original
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = true;
      input.accept = '.pdf,.png,.jpg,.jpeg,.doc,.docx,.txt';
      input.onchange = (e) => {
        const files = Array.from((e.target as HTMLInputElement).files || []);
        if (files.length > 0) {
          const selectedFileData = files[0];
          
          // Aplicar mesma lógica: imagem vai para selectedFile, outros para pendingFiles
          if (selectedFileData.type.startsWith('image/')) {
            setSelectedFile(selectedFileData);
          } else {
            // Para PDF/DOC/TXT: adicionar ao pendingFiles (miniatura com tumb_doc.png)
            setPendingFiles(prev => [...prev, selectedFileData]);
            
            // Notificar o callback se existir
            if (onNonImageFileSelected) {
              onNonImageFileSelected(selectedFileData);
            }
          }
        }
      };
      input.click();
    }
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
  };

  // Processar arquivos pendentes quando uma mensagem é enviada
  const handlePendingFilesProcessing = async () => {
    if (!user || pendingFiles.length === 0) return;

    setIsFileProcessing(true);
    setProcessingStage('thinking');
    setProcessingProgress(0);

    try {
      // ✅ Garantir conversa existente (idempotente)
      let conversationId = await ensureConversation();
      
      if (!conversationId) {
        console.error('❌ Failed to ensure conversation for file upload');
        toast({
          title: "Erro",
          description: "Não foi possível criar uma nova conversa.",
          variant: "destructive",
        });
        return;
      }
      
      console.log('✅ Conversation ready for file upload:', conversationId);

      // Import the processing function
      const { processMultipleFilesAndCreateMessage } = await import("@/context/chat/multipleFileProcessor");
      const { generateAIResponse } = await import("@/context/chat/aiResponseService");

      console.log(`🚀 Processing ${pendingFiles.length} pending files for conversation:`, conversationId);
      
      // 🔑 Gerar IDs de rastreamento para o batch
      const batchRequestId = crypto.randomUUID();
      const batchOriginMessageId = crypto.randomUUID();
      
      const result = await processMultipleFilesAndCreateMessage(
        pendingFiles,
        inputValue || `Análise avançada de ${pendingFiles.length} documentos`,
        conversationId,
        user.id,
        batchRequestId,
        batchOriginMessageId,
        (progress) => {
          setProcessingProgress((progress.filter(p => p.status === 'completed').length / progress.length) * 100);
        }
      );

      console.log('📤 Multi-file processing complete, result:', { 
        hasAutoSummaryMessage: !!result.autoSummaryMessage,
        totalCharacters: result.totalCharacters,
        hasDocumentAnalysis: !!result.documentAnalysis 
      });

      // 🔧 CORREÇÃO: Forçar fetch das mensagens para sincronizar com banco  
      if (currentConversation?.id) {
        console.log('📤 Fetching messages to sync with database after processing');
        await fetchMessages(currentConversation.id);
      }

      toast({
        title: "Análise Concluída!",
        description: `${pendingFiles.length} arquivo(s) processados com análise avançada.`,
        variant: "default",
      });

    } catch (error) {
      console.error('❌ Multi-file processing error:', error);
      toast({
        title: "Erro no Processamento",
        description: "Houve um problema durante o processamento dos arquivos.",
        variant: "destructive",
      });
    } finally {
      setIsFileProcessing(false);
      setProcessingStage('completed');
    }
  };

  // Gerenciar arquivos pendentes
  const handleRemovePendingFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Voice recording integration
  const {
    isRecording,
    isTranscribing,
    duration,
    startRecording,
    stopRecording,
  } = useVoiceRecording({
    onTranscriptionComplete: (text) => {
      setInputValue(text);
      // Auto-send after transcription
      setTimeout(() => {
        handleSendMessage();
      }, 500);
    },
    onError: (error) => {
      toast({
        title: "Erro na Gravação",
        description: error,
        variant: "destructive",
      });
    }
  });

  const handleVoiceRecording = async (audioBlob: Blob, duration: number) => {
    console.log('Voice recording completed:', duration);
    // This is handled by the hook above
  };

  const handlePromptBoxSend = async (message: string, files: File[] = []) => {
    // 🎯 CRÍTICO: usar message OU inputValue como fallback
    const messageText = message || inputValue;
    if (!messageText.trim()) return;
    
    // Handle files - separate images from documents  
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    const docFiles = files.filter(f => !f.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      setSelectedFile(imageFiles[0]);
    }
    
    if (docFiles.length > 0) {
      setPendingFiles(prev => [...prev, ...docFiles]);
    }
    
    // ✅ Chama handleSendMessage com o texto correto
    await handleSendMessage(messageText, imageFiles[0]);
    
    // 🧹 SINCRONIZAÇÃO: garantir que inputValue seja limpo (segurança adicional)
    setInputValue("");
  };

  return (
    <div className="border-t bg-card px-4 py-3">
      {/* Drag & Drop Indicator */}
      {isDragOver && (
        <div className="mb-4 p-6 border-2 border-dashed border-primary/50 rounded-lg bg-primary/5 text-center">
          <p className="text-primary font-medium">Solte os arquivos aqui</p>
          <p className="text-sm text-primary/70">PDF, DOC, TXT, PNG, JPG (até 50MB por arquivo)</p>
        </div>
      )}

      <div className="max-w-[50%] mx-auto">
        <ChatEnterInput
          onSend={async (text) => {
            await handleSendMessage(text);
          }}
          isLoading={isProcessing || isFileProcessing || isTranscribing}
          disabled={!canSend || !isUserApproved}
          placeholder={
            !isUserApproved 
              ? "Chat bloqueado" 
              : !canSend 
                ? `Aguarde ${timeLeft} para enviar nova mensagem`
                : isFileProcessing
                  ? "🔍 Processando documentos..."
                  : pendingFiles.length > 0
                      ? `${pendingFiles.length} arquivo(s) prontos - Digite sua mensagem...`
                      : "Faça perguntas, envie documentos ou cole imagens (Ctrl+V)..."
          }
          selectedFile={selectedFile}
          onFileSelect={setSelectedFile}
          imagePreviews={imagePreviews}
          onRemoveImage={handleRemoveSpecificImage}
          pendingFiles={pendingFiles}
          onRemovePendingFile={handleRemovePendingFile}
          onAttachmentClick={handleAttachmentClick}
          onVoiceRecording={handleVoiceRecording}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onPaste={handlePasteFile}
          className={isDragOver ? 'border-primary/30 bg-primary/5' : ''}
          isRecording={isRecording}
          isTranscribing={isTranscribing}
          duration={duration}
          onStartRecording={startRecording}
          onStopRecording={stopRecording}
        />
      </div>
      
      <div className="text-xs text-center text-muted-foreground mt-2">
        {selectedFile ? (
          <span>
            Pronto para analisar: <span className="font-medium">{selectedFile.name}</span>
          </span>
        ) : pendingFiles.length > 0 ? (
          <span className="text-primary font-medium">
            {pendingFiles.length} arquivo(s) pronto(s) para envio. Digite uma mensagem para processar.
          </span>
        ) : (
          <span>
            {!isUserApproved 
              ? "Seu acesso ao chat está bloqueado. Entre em contato com o suporte."
              : !canSend 
                ? "Plano gratuito: limite de 3 mensagens por dia atingido. Faça upgrade para mensagens ilimitadas."
                : "O Justiçai pode analisar documentos jurídicos. Use o clipe para upload, ou cole imagens com Ctrl+V."}
          </span>
        )}
      </div>

      {/* Processing Progress Bar */}
      {isFileProcessing && (
        <div className="px-4 py-3">
          <div className="bg-muted/50 border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-2 w-2 bg-primary rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">
                {isTranscribing ? 'Transcrevendo áudio...' :
                 processingStage === 'thinking' ? 'Pensando...' : 
                 processingStage === 'extracting' ? 'Extraindo texto...' : 
                 processingStage === 'analyzing' ? 'Analisando documento...' : 
                 processingStage === 'completed' ? 'Concluído!' : 'Processando...'}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300" 
                style={{ width: `${processingProgress}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {isTranscribing ? 'Convertendo sua fala em texto...' : 'Por favor, aguarde enquanto analisamos seu documento...'}
            </p>
          </div>
        </div>
      )}

      {/* Token Limit Dialog */}
      <TokenLimitDialog 
        open={showTokenLimitDialog && limitDialogType === 'credits'} 
        onOpenChange={setShowTokenLimitDialog} 
      />

      {/* Storage/Credits Limit Dialog */}
      <LimitReachedDialog
        open={showStorageLimitDialog || (showTokenLimitDialog && limitDialogType === 'credits')}
        onOpenChange={(open) => {
          setShowStorageLimitDialog(open);
          setShowTokenLimitDialog(open);
        }}
        type={limitDialogType}
      />
    </div>
  );
};

export default ChatInput;
